import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { V2_ENDPOINT } from "../consts";
import { authenticatedFetch } from "../lib/authenticatedFetch";
import type { Tag } from "../stores/templatesStore";

interface TagsApiResponse {
  code: number;
  message: string;
  data: {
    items: Tag[];
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  requestId: string;
}

export type TagScope =
  | "all"
  | "templates_public"
  | "templates_promoted"
  | "templates_org"
  | "actions"
  | "triggers"
  | "connectors";
export interface UseTagsOptions {
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sortByCount?: boolean;
  scope?: TagScope;
  /** When false, skip the fetch entirely. Defaults to true. */
  enabled?: boolean;
}

async function fetchTags(options: {
  category?: string;
  search?: string;
  limit: number;
  offset: number;
  sortByCount: boolean;
  scope: TagScope;
}): Promise<{ items: Tag[]; hasMore: boolean }> {
  const { category, search, limit, offset, sortByCount, scope } = options;

  const url = new URL(`${V2_ENDPOINT}/v1/tags`);
  if (category) {
    url.searchParams.set("category", category);
  }
  if (search) {
    url.searchParams.set("search", search);
  }
  url.searchParams.set("limit", limit.toString());
  if (offset > 0) {
    url.searchParams.set("offset", offset.toString());
  }
  if (sortByCount) {
    url.searchParams.set("sortByCount", "true");
  }
  if (scope !== "all") {
    url.searchParams.set("scope", scope);
  }

  const response = await authenticatedFetch(url.toString(), {
    skipAuth: scope !== "templates_org",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch tags: ${response.statusText}`);
  }

  const data: TagsApiResponse = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return {
    items: data.data.items || [],
    hasMore: data.data.hasMore ?? false,
  };
}

/**
 * Hook to fetch available tags from the API.
 * Uses TanStack Query for automatic caching and deduplication.
 *
 * @param options - Optional configuration object
 * @param options.category - Filter tags by category (e.g., "blockchain", "finance", "App")
 * @param options.search - Full-text search query (searches name and description)
 * @param options.limit - Maximum number of tags to return (default: 24, max: 24)
 * @param options.offset - Number of tags to skip for pagination (default: 0)
 */
export function useTags(options?: UseTagsOptions) {
  const category = options?.category;
  const search = options?.search;
  const limit = options?.limit !== undefined ? options.limit : 24;
  const offset = options?.offset || 0;
  const sortByCount = options?.sortByCount ?? false;
  const scope = options?.scope || "all";
  const enabled = options?.enabled ?? true;

  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", { category, search, limit, offset, sortByCount, scope }],
    queryFn: () => fetchTags({ category, search, limit, offset, sortByCount, scope }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    tags: data?.items ?? [],
    hasMore: data?.hasMore ?? false,
    loading: isLoading,
    error: error instanceof Error ? error : null,
  };
}
